import { constants } from '@wsb/guac-widget-core';
import { TREATMENTS } from './constants';
import { isLightSection } from './utils';

const {
  buttons: { fills, shapes, decorations, shadows }
} = constants;

const GLASS_BLUR = 'blur(12px) saturate(110%)';
const GLASS_BUTTON_TEXT_COLOR = 'rgba(17, 17, 17, 0.92)';
const GLASS_BUTTON_DARK_TEXT_COLOR = 'rgba(255, 255, 255, 0.95)';
const GLASS_LIGHT_BORDER = 'rgba(60, 68, 74, 0.48)';
const GLASS_LIGHT_BORDER_HOVER = 'rgba(60, 68, 74, 0.58)';
const GLASS_LIGHT_BORDER_ACTIVE = 'rgba(60, 68, 74, 0.66)';
const BUTTON_LETTER_SPACING = '0.02em';
export const BUTTON_RADIUS_BY_SHAPE = {
  [shapes.SQUARE]: 0,
  [shapes.ROUND]: 4,
  [shapes.SOFT]: 12,
  [shapes.PILL]: 48
};

export const BUTTON_LABEL_STYLE = {
  fontWeight: 'bold',
  letterSpacing: BUTTON_LETTER_SPACING
};

const GLASS_BUTTON_BASE_STYLE = {
  ...BUTTON_LABEL_STYLE,
  borderWidth: 1,
  borderStyle: 'solid',
  color: GLASS_BUTTON_TEXT_COLOR,
  backdropFilter: GLASS_BLUR,
  WebkitBackdropFilter: GLASS_BLUR,
  backgroundColor: 'transparent',
  overflow: 'hidden',
  textShadow: 'none',
  transition:
    'transform 180ms ease, box-shadow 180ms ease, background 180ms ease, border-color 180ms ease'
};

const GLASS_BUTTON_DARK_STYLE = {
  ...GLASS_BUTTON_BASE_STYLE,
  color: GLASS_BUTTON_DARK_TEXT_COLOR,
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  backgroundImage:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0.11) 44%, rgba(255, 255, 255, 0.05) 100%)',
  borderColor: 'rgba(255, 255, 255, 0.52)',
  boxShadow: [
    'inset 0 1px 1px rgba(255, 255, 255, 0.46)',
    'inset 0 -1px 0 rgba(255, 255, 255, 0.16)',
    '0 6px 14px rgba(0, 0, 0, 0.14)'
  ].join(', '),
  [':hover']: {
    color: GLASS_BUTTON_DARK_TEXT_COLOR,
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    backgroundImage:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.14) 44%, rgba(255, 255, 255, 0.07) 100%)',
    borderColor: 'rgba(255, 255, 255, 0.66)',
    boxShadow: [
      'inset 0 1px 1px rgba(255, 255, 255, 0.54)',
      'inset 0 -1px 0 rgba(255, 255, 255, 0.20)',
      '0 8px 18px rgba(0, 0, 0, 0.18)'
    ].join(', '),
    transform: 'translateY(-2px)'
  },
  [':active']: {
    color: GLASS_BUTTON_DARK_TEXT_COLOR,
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    backgroundImage:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.08) 48%, rgba(255, 255, 255, 0.04) 100%)',
    borderColor: 'rgba(255, 255, 255, 0.48)',
    boxShadow: 'inset 0 3px 8px rgba(0, 0, 0, 0.20), 0 2px 6px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(1px)'
  }
};

const GLASS_BUTTON_LIGHT_STYLE = {
  ...GLASS_BUTTON_BASE_STYLE,
  background:
    'linear-gradient(135deg, rgba(17, 17, 17, 0.08) 0%, rgba(17, 17, 17, 0.04) 46%, rgba(17, 17, 17, 0.02) 100%)',
  borderColor: GLASS_LIGHT_BORDER,
  boxShadow: [
    'inset 0 1px 1px rgba(255, 255, 255, 0.82)',
    'inset 0 -1px 0 rgba(17, 17, 17, 0.08)',
    '0 6px 14px rgba(0, 0, 0, 0.12)'
  ].join(', '),
  [':hover']: {
    color: GLASS_BUTTON_TEXT_COLOR,
    background:
      'linear-gradient(135deg, rgba(17, 17, 17, 0.10) 0%, rgba(17, 17, 17, 0.05) 46%, rgba(17, 17, 17, 0.025) 100%)',
    borderColor: GLASS_LIGHT_BORDER_HOVER,
    boxShadow: [
      'inset 0 1px 1px rgba(255, 255, 255, 0.90)',
      'inset 0 -1px 0 rgba(17, 17, 17, 0.10)',
      '0 8px 18px rgba(0, 0, 0, 0.16)'
    ].join(', '),
    transform: 'translateY(-2px)'
  },
  [':active']: {
    color: GLASS_BUTTON_TEXT_COLOR,
    background:
      'linear-gradient(135deg, rgba(17, 17, 17, 0.12) 0%, rgba(17, 17, 17, 0.07) 50%, rgba(17, 17, 17, 0.035) 100%)',
    borderColor: GLASS_LIGHT_BORDER_ACTIVE,
    boxShadow: 'inset 0 3px 8px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.10)',
    transform: 'translateY(1px)'
  }
};

function getGlassButtonStyle(theme, colorMode) {
  const isLight = isLightSection(theme, colorMode);
  // Label: near-dark on light sections, near-white on dark sections. isLightSection is
  // already color-mode-aware, so this gives max contrast per mode with no fragile
  // var/context resolution. The glass surface, gradient, white highlight and rim in
  // GLASS_BUTTON_*_STYLE are left untouched.
  const textColor = isLight ? GLASS_BUTTON_TEXT_COLOR : GLASS_BUTTON_DARK_TEXT_COLOR;
  const style = isLight ? GLASS_BUTTON_LIGHT_STYLE : GLASS_BUTTON_DARK_STYLE;

  return {
    ...style,
    color: textColor,
    [':hover']: {
      ...style[':hover'],
      color: textColor
    },
    [':active']: {
      ...style[':active'],
      color: textColor
    }
  };
}

function getDiagonalStripedButtonStyle(borderRadius) {
  const radius = borderRadius != null ? borderRadius : 4;
  return {
    position: 'relative',
    borderRadius: radius,
    transformStyle: 'preserve-3d',
    transform: 'rotate(-3deg)',
    transition: 'transform 0.1s ease',
    // Only apply on true hover pointers. Touch devices emulate :hover on tap and
    // "stick" it until the next tap, so gating on (hover: hover) keeps the rest state on mobile.
    ['@media (hover: hover) and (pointer: fine)']: {
      [':hover']: {
        transform: 'rotate(0deg)'
      }
    }
  };
}

function getMappedCssValue(theme, propName, token) {
  const value = theme.mapPropValue(propName, token);
  return value && typeof value.toString === 'function' ? value.toString() : value;
}

function getTextIconButtonStyle(theme, borderRadius) {
  const fallbackIconBackground = getMappedCssValue(theme, 'backgroundColor', 'section');
  const fallbackIconColor = getMappedCssValue(theme, 'color', 'highContrast');

  return {
    position: 'relative',
    paddingHorizontal: 'xsmall',
    paddingVertical: '0',
    ['.iconWrapper']: {
      display: 'flex',
      alignItems: 'stretch',
      width: '100%'
    },
    ['.iconBox']: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
      flexShrink: 0,
      width: 'auto',
      height: '45px',
      aspectRatio: '1 / 1',
      borderRadius: borderRadius || 0,
      backgroundColor: `!var(--wsb-button-fg, ${fallbackIconBackground})`,
      color: `!var(--wsb-button-bg, ${fallbackIconColor})`,
      transform: 'none'
    },
    ['.iconBox svg']: {
      transform: 'rotate(0deg)',
      transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)'
    },
    // Only apply on true hover pointers. Touch devices emulate :hover on tap and
    // "stick" it until the next tap, so gating on (hover: hover) keeps the rest state on mobile.
    ['@media (hover: hover) and (pointer: fine)']: {
      [':hover']: {
        ['.iconBox svg']: {
          transform: 'rotate(-45deg)'
        }
      }
    }
  };
}

const WAVY_BUTTON_STYLE = {
  overflow: 'hidden',
  marginHorizontal: 'small',
  paddingHorizontal: 0,
  paddingVertical: 'small'
};

export function getButtonRadiusStyle(shape) {
  return Object.prototype.hasOwnProperty.call(BUTTON_RADIUS_BY_SHAPE, shape)
    ? { borderRadius: BUTTON_RADIUS_BY_SHAPE[shape] }
    : {};
}

const BUTTON_TREATMENTS = {
  [TREATMENTS.SOLID](theme, props) {
    // NOTE: do not set `shape` here. Radius is an independent axis driven by
    // the option/config `shape` (via handleShape). Forcing a shape in the
    // treatment collapses every radius option to the same corner.
    return theme.merge({}, props, {
      fill: fills.SOLID,
      decoration: decorations.NONE,
      shadow: shadows.NONE,
      style: BUTTON_LABEL_STYLE
    });
  },

  [TREATMENTS.OUTLINE](theme, props) {
    // See SOLID: shape/radius is owned by the option, not the treatment.
    return theme.merge({}, props, {
      fill: fills.GHOST,
      decoration: decorations.NONE,
      shadow: shadows.NONE,
      style: BUTTON_LABEL_STYLE
    });
  },

  [TREATMENTS.LINK](theme, props) {
    return theme.merge({}, props, {
      fill: fills.OPEN,
      decoration: decorations.UNDERLINE,
      shadow: shadows.NONE,
      style: BUTTON_LABEL_STYLE
    });
  },

  [TREATMENTS.GLASS](theme, props) {
    const { colorMode, ...buttonProps } = props;
    return theme.merge({}, buttonProps, {
      fill: fills.NONE,
      decoration: decorations.NONE,
      shadow: shadows.NONE,
      style: {
        ...getGlassButtonStyle(theme, colorMode),
        ...getButtonRadiusStyle(props.shape)
      }
    });
  },

  [TREATMENTS.DIAGONAL_STRIPED](theme, props) {
    const shape = props.shape || (theme.getButtonDefaults && theme.getButtonDefaults().shape);
    const { borderRadius } = getButtonRadiusStyle(shape);
    return theme.merge({}, props, {
      fill: fills.SOLID,
      decoration: decorations.NONE,
      shadow: shadows.DIAGONAL_STRIPED_SHADOW,
      style: { ...BUTTON_LABEL_STYLE, ...getDiagonalStripedButtonStyle(borderRadius) }
    });
  },

  [TREATMENTS.TEXT_ICON](theme, props) {
    const shape = props.shape || (theme.getButtonDefaults && theme.getButtonDefaults().shape);
    const { borderRadius } = getButtonRadiusStyle(shape);
    return theme.merge({}, props, {
      fill: fills.SOLID,
      decoration: decorations.NONE,
      shadow: shadows.NONE,
      improvedIconButton: true,
      rightIcon: 'arrowRight',
      iconOnLeft: false,
      style: {
        ...BUTTON_LABEL_STYLE,
        ...getButtonRadiusStyle(shape),
        ...getTextIconButtonStyle(theme, borderRadius)
      }
    });
  },

  [TREATMENTS.WAVY](theme, props) {
    return theme.merge({}, props, {
      fill: fills.OPEN,
      decoration: decorations.WAVY_UNDERLINE,
      shadow: shadows.NONE,
      style: { ...BUTTON_LABEL_STYLE, ...WAVY_BUTTON_STYLE }
    });
  }
};

export function resolveButtonTreatment(theme, props = {}) {
  const { treatment, ...buttonProps } = props;
  const treatmentResolver = treatment && BUTTON_TREATMENTS[treatment];
  if (!treatmentResolver) return props;
  return treatmentResolver(theme, buttonProps);
}
